// ADV-MKT-PREDEPLOY-003A — Track A1 request coalescing.
//
// Collapses identical concurrent / near-concurrent GET requests into a single
// in-flight network call plus a very short result cache. Components that
// independently fetch the SAME url within ttlMs receive the SAME parsed JSON,
// so rendered values are byte-for-byte identical to the pre-change behavior —
// only the redundant network round-trips are removed.
//
// Two sharing modes:
//   * In-flight dedup (ALWAYS): an unsettled request is shared by every caller,
//     so a refresh that fans out to several panels on the same tick still makes
//     one network call.
//   * Short result cache (initial-load only): a recently-resolved result is
//     reused for ttlMs. A user-triggered refresh passes bypassCache=true so it
//     re-hits the network for fresh data (never serves stale on explicit
//     refresh) while still sharing the in-flight request with its sibling panel.
//
// Scope: read-only GET JSON only. No mutation / order endpoints. The cache is
// an in-memory module singleton (per browser tab); it never persists to disk.
export type CoalescedResult = { ok: boolean; status: number; json: any | null; error?: string };

type Entry = { at: number; settled: boolean; promise: Promise<CoalescedResult> };

const inflight = new Map<string, Entry>();

export function coalescedGetJson(url: string, ttlMs = 3500, bypassCache = false): Promise<CoalescedResult> {
  const now = Date.now();
  const existing = inflight.get(url);
  // Always share an in-flight (unsettled) request.
  if (existing && !existing.settled) return existing.promise;
  // Reuse a recently-resolved result only for non-bypass (initial-load) callers.
  if (existing && existing.settled && !bypassCache && now - existing.at <= ttlMs) {
    return existing.promise;
  }
  const entry: Entry = { at: now, settled: false, promise: Promise.resolve({ ok: false, status: 0, json: null }) };
  entry.promise = (async (): Promise<CoalescedResult> => {
    try {
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return { ok: false, status: res.status, json: null };
      const json = await res.json();
      return { ok: true, status: res.status, json };
    } catch (e: any) {
      return { ok: false, status: 0, json: null, error: String(e?.message || e) };
    } finally {
      entry.settled = true;
      entry.at = Date.now();
      setTimeout(() => { if (inflight.get(url) === entry) inflight.delete(url); }, ttlMs);
    }
  })();
  inflight.set(url, entry);
  return entry.promise;
}

export default coalescedGetJson;
